import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Telescope, Trash2, Loader2, X, GripVertical, Save, Pencil, Copy, Check } from 'lucide-react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
// Drop indicator placed in the gap above/below the row so the cue reads as
// "between rows" rather than an accent on a single row. Mirrors the same
// pattern used in UserFolderSection.
const DropLine = ({ side }) => (_jsx("div", { "aria-hidden": true, style: {
        position: 'absolute',
        left: 4,
        right: 4,
        height: 2,
        borderRadius: 1,
        backgroundColor: 'var(--fern, #5db075)',
        boxShadow: '0 0 0 1px rgba(93, 176, 117, 0.25)',
        pointerEvents: 'none',
        ...(side === 'top' ? { top: -3 } : { bottom: -3 }),
    } }));
const SortableScopeRow = ({ scope, isActive, isRenaming, renameDraft, onRenameDraftChange, onStartRename, onCommitRename, onCancelRename, onApply, onOverride, onDuplicate, onRequestDelete, }) => {
    const { attributes, listeners, setNodeRef, isDragging, isOver, activeIndex, index } = useSortable({
        id: scope.id,
        disabled: isRenaming,
    });
    const sameSortable = activeIndex >= 0;
    const showIndicator = isOver && !isDragging && sameSortable && activeIndex !== index;
    const indicatorSide = !showIndicator
        ? null
        : activeIndex < index ? 'bottom' : 'top';
    const renameInputRef = useRef(null);
    useEffect(() => {
        var _a, _b;
        if (isRenaming) {
            (_a = renameInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = renameInputRef.current) === null || _b === void 0 ? void 0 : _b.select();
        }
    }, [isRenaming]);
    const handleRenameKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onCommitRename();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onCancelRename();
        }
    };
    return (_jsxs("div", { ref: setNodeRef, style: {
            opacity: isDragging ? 0.3 : 1,
            position: 'relative',
            borderRadius: 4,
        }, className: "group", children: [_jsxs("div", { className: "flex items-center gap-1 px-1 py-1.5 rounded text-sm transition-colors", style: {
                    backgroundColor: isActive ? 'rgba(255,255,255,0.10)' : 'var(--pewter)',
                    color: 'var(--sand)',
                    border: isActive ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent',
                    boxShadow: isActive ? 'inset 0 1px 4px rgba(0,0,0,0.4)' : 'none',
                }, children: [_jsx("button", { type: "button", ...attributes, ...listeners, className: "touch-none cursor-grab active:cursor-grabbing opacity-30 group-hover:opacity-100 transition-opacity", style: { color: 'var(--dew)', background: 'none', border: 'none', padding: 2 }, "aria-label": "Drag to reorder scope", title: "Drag to reorder", onClick: (e) => e.preventDefault(), disabled: isRenaming, children: _jsx(GripVertical, { size: 12 }) }), _jsx(Telescope, { size: 14, style: { color: 'var(--fern)', flexShrink: 0 } }), isRenaming ? (_jsx("input", { ref: renameInputRef, type: "text", value: renameDraft, onChange: (e) => onRenameDraftChange(e.target.value), onBlur: onCommitRename, onKeyDown: handleRenameKey, className: "flex-1 min-w-0 px-1 py-0.5 text-sm rounded border-0 outline-none", style: { backgroundColor: 'var(--charcoal)', color: 'var(--sand)' } })) : (_jsx("button", { type: "button", onClick: onApply, onDoubleClick: onStartRename, className: "flex-1 min-w-0 text-left truncate cursor-pointer", style: { background: 'none', border: 'none', color: 'inherit', padding: 0 }, title: isActive ? 'Active scope' : 'Click to apply, double-click to rename', children: scope.name })), isActive && !isRenaming && (_jsx("span", { className: "text-xs flex-shrink-0", style: { color: 'var(--fern)', opacity: 0.7 }, children: "Active" })), !isRenaming ? (_jsxs("div", { className: "flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0", children: [_jsx(RowIconButton, { icon: _jsx(Save, { size: 13 }), title: "Save current view onto this scope", onClick: onOverride }), _jsx(RowIconButton, { icon: _jsx(Pencil, { size: 13 }), title: "Rename scope", onClick: onStartRename }), _jsx(RowIconButton, { icon: _jsx(Copy, { size: 13 }), title: "Duplicate scope", onClick: onDuplicate }), _jsx(RowIconButton, { icon: _jsx(Trash2, { size: 13 }), title: "Delete scope", hoverColor: "var(--error-red)", onClick: onRequestDelete })] })) : (_jsxs("div", { className: "flex items-center gap-0.5 flex-shrink-0", children: [_jsx(RowIconButton, { icon: _jsx(Check, { size: 13 }), title: "Save rename (Enter)", hoverColor: "var(--fern)", onMouseDown: (e) => {
                                    // Prevent the input's blur from firing onCommitRename before
                                    // this onMouseDown; we still call commit explicitly below.
                                    e.preventDefault();
                                }, onClick: onCommitRename }), _jsx(RowIconButton, { icon: _jsx(X, { size: 13 }), title: "Cancel rename (Esc)", onMouseDown: (e) => e.preventDefault(), onClick: onCancelRename })] }))] }), indicatorSide && _jsx(DropLine, { side: indicatorSide })] }));
};
const RowIconButton = ({ icon, title, onClick, hoverColor, onMouseDown }) => (_jsx("button", { type: "button", title: title, onClick: (e) => {
        e.stopPropagation();
        onClick();
    }, onMouseDown: onMouseDown, className: "p-1 rounded transition-colors", style: { background: 'none', border: 'none', color: 'var(--dew)' }, onMouseEnter: (e) => {
        e.currentTarget.style.color = hoverColor !== null && hoverColor !== void 0 ? hoverColor : 'var(--sand)';
    }, onMouseLeave: (e) => {
        e.currentTarget.style.color = 'var(--dew)';
    }, children: icon }));
export const ScopesSection = ({ scopes, loading, activeScopeId, onCapture, onApply, onClear, onDelete, onOverride, onRename, onDuplicate, onReorder, }) => {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [deleteConfirmScope, setDeleteConfirmScope] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [renameDraft, setRenameDraft] = useState('');
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const trimmedInput = inputValue.trim();
    const suggestions = useMemo(() => {
        if (!trimmedInput)
            return [];
        return scopes.filter((s) => s.name.toLowerCase().includes(trimmedInput.toLowerCase()));
    }, [scopes, trimmedInput]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (suggestionsRef.current &&
                !suggestionsRef.current.contains(e.target) &&
                inputRef.current &&
                !inputRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const handleSubmit = () => {
        if (!trimmedInput)
            return;
        onCapture(trimmedInput);
        setInputValue('');
        setShowSuggestions(false);
    };
    const handleKeyDown = (e) => {
        var _a;
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
        if (e.key === 'Escape') {
            setShowSuggestions(false);
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
    };
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const scopeIds = useMemo(() => scopes.map((s) => s.id), [scopes]);
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const oldIndex = scopeIds.indexOf(active.id);
        const newIndex = scopeIds.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1)
            return;
        const nextIds = arrayMove(scopeIds, oldIndex, newIndex);
        onReorder(nextIds);
    }, [scopeIds, onReorder]);
    const startRename = (scope) => {
        setRenamingId(scope.id);
        setRenameDraft(scope.name);
    };
    const commitRename = () => {
        if (!renamingId)
            return;
        const target = scopes.find((s) => s.id === renamingId);
        const next = renameDraft.trim();
        if (target && next && next !== target.name) {
            onRename(renamingId, next);
        }
        setRenamingId(null);
        setRenameDraft('');
    };
    const cancelRename = () => {
        setRenamingId(null);
        setRenameDraft('');
    };
    return (_jsxs("div", { children: [_jsx("div", { className: "px-2 mb-2 text-xs", style: { color: 'var(--dew)' }, children: "Save the current camera position and layer setup as a scope. Click a scope to restore it; hover for rename, duplicate, and save-here actions." }), _jsxs("div", { className: "relative", children: [_jsxs("div", { className: "flex gap-1", children: [_jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: (e) => {
                                    setInputValue(e.target.value);
                                    setShowSuggestions(true);
                                }, onFocus: () => setShowSuggestions(true), onKeyDown: handleKeyDown, placeholder: "New scope name...", className: "flex-1 px-2 py-1.5 text-sm rounded border-0 outline-none", style: {
                                    backgroundColor: 'var(--pewter)',
                                    color: 'var(--sand)',
                                } }), _jsx("button", { onClick: handleSubmit, disabled: !trimmedInput, className: "px-3 py-1.5 text-xs font-medium rounded transition-colors", style: {
                                    backgroundColor: trimmedInput ? 'var(--fern)' : 'var(--pewter)',
                                    color: trimmedInput ? 'var(--forest)' : 'var(--dew)',
                                    cursor: trimmedInput ? 'pointer' : 'default',
                                }, children: "Create" })] }), showSuggestions && suggestions.length > 0 && (_jsx("div", { ref: suggestionsRef, className: "absolute left-0 right-0 top-full mt-1 rounded overflow-hidden z-20 max-h-32 overflow-y-auto", style: { backgroundColor: 'var(--charcoal)' }, children: suggestions.map((scope) => (_jsx("button", { onClick: () => {
                                setInputValue(scope.name);
                                setShowSuggestions(false);
                            }, className: "w-full text-left px-3 py-1.5 text-xs transition-colors", style: { color: 'var(--sand)' }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--pewter)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, children: scope.name }, scope.id))) }))] }), activeScopeId && onClear && (_jsx("div", { className: "mt-2", children: _jsxs("button", { onClick: onClear, className: "w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded transition-colors", style: {
                        backgroundColor: 'var(--pewter)',
                        color: 'var(--sand)',
                        border: '1px solid var(--fern)',
                    }, children: [_jsx(X, { size: 12 }), "Clear active scope"] }) })), loading ? (_jsxs("div", { className: "flex items-center gap-2 mt-3 px-2", style: { color: 'var(--dew)' }, children: [_jsx(Loader2, { size: 14, className: "animate-spin" }), _jsx("span", { className: "text-xs", children: "Loading scopes..." })] })) : scopes.length === 0 ? (_jsx("div", { className: "mt-3 px-2 text-xs", style: { color: 'var(--dew)' }, children: "No scopes saved. Type a name above and click Create." })) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: scopeIds, strategy: verticalListSortingStrategy, children: _jsx("div", { className: "mt-2 space-y-1", children: scopes.map((scope) => (_jsx(SortableScopeRow, { scope: scope, isActive: activeScopeId === scope.id, isRenaming: renamingId === scope.id, renameDraft: renameDraft, onRenameDraftChange: setRenameDraft, onStartRename: () => startRename(scope), onCommitRename: commitRename, onCancelRename: cancelRename, onApply: () => onApply(scope.id), onOverride: () => onOverride(scope.id), onDuplicate: () => onDuplicate(scope.id), onRequestDelete: () => setDeleteConfirmScope({ id: scope.id, name: scope.name }) }, scope.id))) }) }) })), _jsx(ConfirmDeleteModal, { isOpen: deleteConfirmScope !== null, itemName: (deleteConfirmScope === null || deleteConfirmScope === void 0 ? void 0 : deleteConfirmScope.name) || '', onConfirm: () => {
                    if (deleteConfirmScope) {
                        onDelete(deleteConfirmScope.id);
                    }
                    setDeleteConfirmScope(null);
                }, onCancel: () => setDeleteConfirmScope(null) })] }));
};
